import React from 'react';
import { useReport } from '../hooks/useReport';

/**
 * Bare distribution table (no card/header of its own — the parent card owns
 * those and the chart toggle). Name / Live / Deleted / Total: live and deleted
 * are separate columns, never blended, per the report's soft-delete invariant.
 */
const ReportTable = ({ dimension, filters }) => {
  const params = React.useMemo(() => ({ dimension, ...filters }), [dimension, filters]);
  const { data, loading, error, refetch } = useReport('/reports/mis/distribution', params);
  const rows = Array.isArray(data) ? data : [];

  const totals = rows.reduce(
    (acc, r) => ({ live: acc.live + (r.value || 0), deleted: acc.deleted + (r.deleted || 0), total: acc.total + (r.total || 0) }),
    { live: 0, deleted: 0, total: 0 }
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="border-b border-dash-border text-dash-muted-foreground">
            <th className="py-1 pr-2 text-left font-medium">Name</th>
            <th className="py-1 px-2 text-right font-medium">Live</th>
            <th className="py-1 px-2 text-right font-medium">Deleted</th>
            <th className="py-1 pl-2 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 ? (
            <tr><td colSpan={4} className="py-3 text-center text-dash-muted-foreground">Loading…</td></tr>
          ) : error ? (
            // A failed fetch (e.g. rate-limited during a burst of page loads) must
            // not masquerade as an empty dimension — say so, and offer a retry.
            <tr>
              <td colSpan={4} className="py-3 text-center text-dash-muted-foreground">
                Couldn't load this table.{' '}
                <button type="button" onClick={refetch} className="font-medium text-dash-chart-1 underline underline-offset-2">
                  Retry
                </button>
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={4} className="py-3 text-center text-dash-muted-foreground">No data available</td></tr>
          ) : (
            rows.map((r, i) => (
              <tr key={i} className="border-b border-dash-border/50">
                <td className="py-1 pr-2 text-dash-foreground">{r.name}</td>
                <td className="py-1 px-2 text-right tabular-nums text-dash-foreground">{(r.value || 0).toLocaleString()}</td>
                <td className="py-1 px-2 text-right tabular-nums text-dash-muted-foreground">{(r.deleted || 0).toLocaleString()}</td>
                <td className="py-1 pl-2 text-right tabular-nums font-medium text-dash-foreground">{(r.total || 0).toLocaleString()}</td>
              </tr>
            ))
          )}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr className="border-t border-dash-border font-medium text-dash-foreground">
              <td className="py-1 pr-2">Total</td>
              <td className="py-1 px-2 text-right tabular-nums">{totals.live.toLocaleString()}</td>
              <td className="py-1 px-2 text-right tabular-nums">{totals.deleted.toLocaleString()}</td>
              <td className="py-1 pl-2 text-right tabular-nums">{totals.total.toLocaleString()}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default ReportTable;
